import { lazy, Suspense, useEffect } from 'react';
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useParams,
} from 'react-router-dom';
import { AdminShell } from './components/admin/admin-shell';
import { DashboardShell } from './components/layout/dashboard-shell';
import {
  RequireActiveSubscription,
  RequireAdmin,
  RequireAuth,
  RequireModule,
  RequireSuperAdmin,
  RequireTenant,
} from './lib/auth-guard';
import { useBootstrapOnMount } from './lib/use-session';
import { useBranding } from './lib/use-branding';

import LandingPage from './pages/landing';
import CheckoutSucessoPage from './pages/checkout-sucesso';
import SignInPage from './pages/sign-in';
import SignUpPage from './pages/sign-up';
import ForgotPasswordPage from './pages/forgot-password';
import ResetPasswordPage from './pages/reset-password';
import VerifyEmailPage from './pages/verify-email';
import MfaSetupPage from './pages/mfa-setup';
import MfaVerifyPage from './pages/mfa-verify';
import AceitarConvitePage from './pages/aceitar-convite';
import PrimeiroAcessoPage from './pages/primeiro-acesso';
import MapaIntegracaoPage from './pages/mapa-integracao';
import SemEscritorioPage from './pages/sem-escritorio';
import DashboardPage from './pages/escritorio/dashboard';
import MembrosPage from './pages/escritorio/membros';
import ConvidarMembroPage from './pages/escritorio/membros-convidar';
import ConfiguracoesLayout from './pages/escritorio/configuracoes';
import { ActionGroupsPage } from './components/office-config/action-groups-page';
import { ActionTypesPage } from './components/office-config/action-types-page';
import { CaseStagesPage } from './components/office-config/case-stages-page';
import { CertificatesPage } from './components/office-config/certificates-page';
import { MonitoredTermsPage } from './components/office-config/monitored-terms-page';
import { MonitoramentosPage } from './components/office-config/monitoramentos-page';
import { TaskCategoriesPage } from './components/office-config/task-categories-page';
import { FinancialConfigPage } from './components/office-config/financial-config-page';
import { NotificationsPreferencesPage } from './components/office-config/notifications-page';
import { OfficeDataPage } from './components/office-config/office-data-page';
import { SubscriptionPage } from './components/office-config/subscription-page';
import { DocumentCategoriesPage } from './components/office-config/document-categories-page';
import { TribunalConnectionsPage } from './components/office-config/tribunal-connections-page';
import { EmailSettingsPage } from './components/office-config/email-settings-page';
import { UsersPage } from './components/office-config/users-page';
import { CustomizationsPage } from './components/office-config/customizations-page';
import { StoragePage } from './components/office-config/storage-page';
import { SystemTourPage } from './components/office-config/system-tour-page';
import ImportacaoIndexPage from './pages/escritorio/importacao/index';
import ImportacaoKindPage from './pages/escritorio/importacao/kind-page';
import ImportacaoTemplatesPage from './pages/escritorio/importacao/templates-page';
import ClientesPage from './pages/escritorio/clientes';
import ProcessosPage from './pages/escritorio/processos';
import IntimacoesPage from './pages/escritorio/intimacoes';
import PortalCaixaEntradaPage from './pages/escritorio/portal-caixa-entrada';
import AtividadesPage from './pages/escritorio/atividades';
import FinanceiroPage from './pages/escritorio/financeiro';
import NotificacoesPage from './pages/escritorio/notificacoes';
import AdminIndexPage from './pages/admin/index';
import AdminClientesPage from './pages/admin/clientes';
import AdminClienteNovoPage from './pages/admin/clientes-novo';
import AdminClienteDetalhePage from './pages/admin/clientes-detalhe';
import AdminAparenciaPage from './pages/admin/aparencia';
import AdminIntegracoesPage from './pages/admin/integracoes';
import AdminPlanosPage from './pages/admin/planos';
import AdminCuponsPage from './pages/admin/cupons';
import AdminArmazenamentoPage from './pages/admin/armazenamento';

// Code-split: a pagina de cadastro/detalhe puxa react-hook-form +
// zod-resolver + 4 secoes pesadas e e a maior pagina do app fora do
// dashboard. Lazy-load mantem o bundle inicial leve (sub-500KB) e ainda
// permite reuso na rota /clientes/:id sem duplicar o codigo.
const ClienteFormPage = lazy(() => import('./pages/escritorio/cliente-form'));
const ProcessoFormPage = lazy(
  () => import('./pages/escritorio/processo-form'),
);
const FinanceiroFormPage = lazy(
  () => import('./pages/escritorio/financeiro-form'),
);
const FinanceiroAgruparImportacoesPage = lazy(
  () => import('./pages/escritorio/financeiro-agrupar-importacoes'),
);
const FinanceiroContratoDetailPage = lazy(
  () => import('./pages/escritorio/financeiro-contrato-detail'),
);
const DocumentosPage = lazy(
  () => import('./pages/escritorio/documentos'),
);
const DocumentoEditorPage = lazy(
  () => import('./pages/escritorio/documento-editor'),
);
const DocumentoInstanciaEditorPage = lazy(
  () => import('./pages/escritorio/documento-instancia-editor'),
);
const InstanciaPrintPage = lazy(
  () => import('./pages/print/instancia-print'),
);
const AgendaPage = lazy(() => import('./pages/escritorio/agenda'));
const RelatoriosPage = lazy(
  () => import('./pages/escritorio/relatorios'),
);
// F12 round 6 — carrega o SDK do Stripe (@stripe/react-stripe-js) so quando
// o visitante entra na pagina de assinatura, mantendo o bundle inicial leve.
const AssinarPage = lazy(() => import('./pages/assinar'));

// Portal do Cliente (docs/PORTAL-CLIENTE.md secao 8) — arvore de rotas
// isolada do escritorio, em bundle proprio. So e importada quando a rota
// bate em /portal/*, decidido em App() ANTES de useBootstrapOnMount()
// rodar (ver cuidado 1 da secao 8: visita do cliente nao pode bater em
// GET /api/auth/me do escritorio).
const PortalApp = lazy(() => import('./portal/portal-app'));

function PageFallback() {
  return (
    <div className="flex min-h-[40vh] items-center justify-center text-sm text-[var(--fg-muted)]">
      Carregando...
    </div>
  );
}

function RedirectInstanceToEditor() {
  const { id } = useParams<{ id: string }>();
  return (
    <Navigate
      to={`/escritorio/documentos/instancias/${id}/editor`}
      replace
    />
  );
}

function NotFoundPage() {
  return (
    <main className="flex min-h-screen items-center justify-center bg-[var(--bg)] px-4 py-12 text-[var(--fg)]">
      <div className="max-w-md text-center">
        <h1 className="mb-2 text-3xl font-semibold">Página não encontrada</h1>
        <p className="text-sm text-[var(--fg-muted)]">
          O endereço que você acessou não existe.
        </p>
      </div>
    </main>
  );
}

function FaviconUpdater() {
  const { branding } = useBranding();

  useEffect(() => {
    if (!branding?.faviconUrl) return;
    const link =
      (document.querySelector("link[rel~='icon']") as HTMLLinkElement | null) ??
      (() => {
        const el = document.createElement('link');
        el.rel = 'icon';
        document.head.appendChild(el);
        return el;
      })();
    link.href = branding.faviconUrl;
  }, [branding?.faviconUrl]);

  return null;
}

function EscritorioApp() {
  useBootstrapOnMount();

  return (
    <BrowserRouter>
      <FaviconUpdater />
      <Routes>
        {/* Publicas */}
        <Route path="/sign-in" element={<SignInPage />} />
        <Route path="/sign-up" element={<SignUpPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route
          path="/reset-password/:token"
          element={<ResetPasswordPage />}
        />
        <Route path="/verify-email/:token" element={<VerifyEmailPage />} />
        <Route path="/mfa-verify" element={<MfaVerifyPage />} />
        <Route
          path="/aceitar-convite/:token"
          element={<AceitarConvitePage />}
        />
        <Route
          path="/primeiro-acesso/:token"
          element={<PrimeiroAcessoPage />}
        />

        {/* Publica: documentacao de integracoes judiciais */}
        <Route path="/mapa-de-integracao" element={<MapaIntegracaoPage />} />

        {/* Publica: retorno do Stripe Checkout (F12 Billing) */}
        <Route path="/checkout/sucesso" element={<CheckoutSucessoPage />} />

        {/* Publica: checkout transparente com Payment Element (F12 round 6) */}
        <Route
          path="/assinar/:planCode"
          element={
            <Suspense fallback={<PageFallback />}>
              <AssinarPage />
            </Suspense>
          }
        />

        {/* Autenticadas mas sem shell (paginas de onboarding/auth-flow) */}
        <Route
          path="/sem-escritorio"
          element={
            <RequireAuth>
              <SemEscritorioPage />
            </RequireAuth>
          }
        />
        <Route
          path="/mfa-setup"
          element={
            <RequireAuth>
              <MfaSetupPage />
            </RequireAuth>
          }
        />
        <Route
          path="/escritorio/documentos/:id/editor"
          element={
            <RequireAuth>
              <RequireTenant>
                <RequireModule module="documents">
                  <Suspense fallback={<PageFallback />}>
                    <DocumentoEditorPage />
                  </Suspense>
                </RequireModule>
              </RequireTenant>
            </RequireAuth>
          }
        />
        <Route
          path="/escritorio/documentos/instancias/:id/editor"
          element={
            <RequireAuth>
              <RequireTenant>
                <RequireModule module="documents">
                  <Suspense fallback={<PageFallback />}>
                    <DocumentoInstanciaEditorPage />
                  </Suspense>
                </RequireModule>
              </RequireTenant>
            </RequireAuth>
          }
        />
        {/*
          Rota dedicada de impressao — standalone (sem DashboardShell,
          sem sidebar, sem header do app). Mantem RequireAuth/RequireTenant
          para nao expor instancias de outros tenants. Abre em _blank,
          renderiza o documento e dispara window.print() automaticamente.
        */}
        <Route
          path="/print/instancia/:id"
          element={
            <RequireAuth>
              <RequireTenant>
                <Suspense fallback={<PageFallback />}>
                  <InstanciaPrintPage />
                </Suspense>
              </RequireTenant>
            </RequireAuth>
          }
        />

        {/* Dashboard do escritorio */}
        <Route
          element={
            <RequireAuth>
              <RequireTenant>
                <RequireActiveSubscription>
                  <DashboardShell />
                </RequireActiveSubscription>
              </RequireTenant>
            </RequireAuth>
          }
        >
          <Route path="/escritorio" element={<DashboardPage />} />
          {/* /membros e /membros/convidar foram unificados em /configuracoes/usuarios */}
          <Route
            path="/escritorio/membros"
            element={<Navigate to="/escritorio/configuracoes/usuarios" replace />}
          />
          <Route
            path="/escritorio/membros/convidar"
            element={<Navigate to="/escritorio/configuracoes/usuarios" replace />}
          />
          <Route
            path="/escritorio/configuracoes"
            element={<RequireAdmin><ConfiguracoesLayout /></RequireAdmin>}
          >
            <Route
              index
              element={
                <Navigate to="/escritorio/configuracoes/grupos-de-acao" replace />
              }
            />
            <Route path="grupos-de-acao" element={<ActionGroupsPage />} />
            <Route path="tipos-de-acao" element={<ActionTypesPage />} />
            <Route path="etapas" element={<CaseStagesPage />} />
            <Route
              path="certificado-digital"
              element={<CertificatesPage />}
            />
            {/* /oabs descontinuada na Rodada B do J08 — redireciona para
                a tela unificada de Monitoramentos. Bookmarks antigos
                continuam funcionando. */}
            <Route
              path="oabs"
              element={
                <Navigate to="/escritorio/configuracoes/monitoramentos" replace />
              }
            />
            <Route
              path="monitoramentos"
              element={<MonitoramentosPage />}
            />
            <Route path="termos" element={<MonitoredTermsPage />} />
            {/* Alias amigavel para o novo rotulo "Alertas por palavra".
                A rota canonica continua sendo /termos para nao quebrar
                bookmarks/links em emails de notificacao. */}
            <Route
              path="alertas-por-palavra"
              element={
                <Navigate
                  to="/escritorio/configuracoes/termos"
                  replace
                />
              }
            />
            <Route
              path="notificacoes"
              element={<NotificationsPreferencesPage />}
            />
            <Route
              path="categorias-tarefas"
              element={<TaskCategoriesPage />}
            />
            <Route
              path="financeiro"
              element={<FinancialConfigPage />}
            />
            <Route
              path="dados-escritorio"
              element={<OfficeDataPage />}
            />
            <Route
              path="assinatura"
              element={<SubscriptionPage />}
            />
            <Route
              path="categorias-documentos"
              element={<DocumentCategoriesPage />}
            />
            <Route path="importacao" element={<ImportacaoIndexPage />} />
            <Route path="importacao/templates" element={<ImportacaoTemplatesPage />} />
            <Route path="importacao/:kind" element={<ImportacaoKindPage />} />
            <Route
              path="conexoes-tribunais"
              element={<TribunalConnectionsPage />}
            />
            <Route path="email" element={<EmailSettingsPage />} />
            <Route path="usuarios" element={<UsersPage />} />
            <Route path="armazenamento" element={<StoragePage />} />
            <Route path="customizacoes" element={<CustomizationsPage />} />
            <Route path="tour" element={<SystemTourPage />} />
          </Route>
          {/* Clientes — modulo 'clients' */}
          <Route
            path="/escritorio/clientes"
            element={<RequireModule module="clients"><ClientesPage /></RequireModule>}
          />
          <Route
            path="/escritorio/clientes/novo"
            element={
              <RequireModule module="clients">
                <Suspense fallback={<PageFallback />}>
                  <ClienteFormPage mode="new" />
                </Suspense>
              </RequireModule>
            }
          />
          <Route
            path="/escritorio/clientes/:id"
            element={
              <RequireModule module="clients">
                <Suspense fallback={<PageFallback />}>
                  <ClienteFormPage mode="edit" />
                </Suspense>
              </RequireModule>
            }
          />

          {/* Caixa de entrada do portal — modulo 'clients' (mesmo guard do backend) */}
          <Route
            path="/escritorio/portal/caixa-entrada"
            element={<RequireModule module="clients"><PortalCaixaEntradaPage /></RequireModule>}
          />

          {/* Processos — modulo 'cases' */}
          <Route
            path="/escritorio/processos"
            element={<RequireModule module="cases"><ProcessosPage /></RequireModule>}
          />
          <Route
            path="/escritorio/processos/novo"
            element={
              <RequireModule module="cases">
                <Suspense fallback={<PageFallback />}>
                  <ProcessoFormPage mode="new" />
                </Suspense>
              </RequireModule>
            }
          />
          <Route
            path="/escritorio/processos/:id"
            element={
              <RequireModule module="cases">
                <Suspense fallback={<PageFallback />}>
                  <ProcessoFormPage mode="edit" />
                </Suspense>
              </RequireModule>
            }
          />

          {/* Intimacoes — modulo 'publications' */}
          <Route
            path="/escritorio/intimacoes"
            element={<RequireModule module="publications"><IntimacoesPage /></RequireModule>}
          />

          {/* Agenda e Tarefas — modulo 'agenda' */}
          <Route
            path="/escritorio/atividades"
            element={<RequireModule module="agenda"><AtividadesPage /></RequireModule>}
          />
          <Route
            path="/escritorio/agenda"
            element={
              <RequireModule module="agenda">
                <Suspense fallback={<PageFallback />}>
                  <AgendaPage />
                </Suspense>
              </RequireModule>
            }
          />

          {/* Notificacoes — sempre visivel */}
          <Route
            path="/escritorio/notificacoes"
            element={<NotificacoesPage />}
          />

          {/* Documentos — modulo 'documents' */}
          <Route
            path="/escritorio/documentos"
            element={
              <RequireModule module="documents">
                <Suspense fallback={<PageFallback />}>
                  <DocumentosPage />
                </Suspense>
              </RequireModule>
            }
          />
          <Route
            path="/escritorio/documentos/instancias/:id"
            element={<RequireModule module="documents"><RedirectInstanceToEditor /></RequireModule>}
          />

          {/* Relatorios — modulo 'reports' */}
          <Route
            path="/escritorio/relatorios"
            element={
              <RequireModule module="reports">
                <Suspense fallback={<PageFallback />}>
                  <RelatoriosPage />
                </Suspense>
              </RequireModule>
            }
          />

          {/* Financeiro — modulo 'financial' */}
          <Route
            path="/escritorio/financeiro"
            element={<RequireModule module="financial"><FinanceiroPage /></RequireModule>}
          />
          <Route
            path="/escritorio/financeiro/novo"
            element={
              <RequireModule module="financial">
                <Suspense fallback={<PageFallback />}>
                  <FinanceiroFormPage mode="new" />
                </Suspense>
              </RequireModule>
            }
          />
          <Route
            path="/escritorio/financeiro/contratos/:id"
            element={
              <RequireModule module="financial">
                <Suspense fallback={<PageFallback />}>
                  <FinanceiroContratoDetailPage />
                </Suspense>
              </RequireModule>
            }
          />
          <Route
            path="/escritorio/financeiro/:id"
            element={
              <RequireModule module="financial">
                <Suspense fallback={<PageFallback />}>
                  <FinanceiroFormPage mode="edit" />
                </Suspense>
              </RequireModule>
            }
          />
          <Route
            path="/escritorio/financeiro/agrupar-importacoes"
            element={
              <RequireModule module="financial">
                <Suspense fallback={<PageFallback />}>
                  <FinanceiroAgruparImportacoesPage />
                </Suspense>
              </RequireModule>
            }
          />
        </Route>

        {/* Admin */}
        <Route
          element={
            <RequireAuth>
              <RequireSuperAdmin>
                <AdminShell />
              </RequireSuperAdmin>
            </RequireAuth>
          }
        >
          <Route path="/admin" element={<AdminIndexPage />} />
          <Route path="/admin/clientes" element={<AdminClientesPage />} />
          <Route
            path="/admin/clientes/novo"
            element={<AdminClienteNovoPage />}
          />
          <Route
            path="/admin/clientes/:id"
            element={<AdminClienteDetalhePage />}
          />
          <Route path="/admin/aparencia" element={<AdminAparenciaPage />} />
          <Route path="/admin/integracoes" element={<AdminIntegracoesPage />} />
          <Route path="/admin/planos" element={<AdminPlanosPage />} />
          <Route path="/admin/cupons" element={<AdminCuponsPage />} />
          <Route path="/admin/armazenamento" element={<AdminArmazenamentoPage />} />
        </Route>

        {/* Root — landing pública de early access */}
        <Route path="/" element={<LandingPage />} />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </BrowserRouter>
  );
}

/**
 * Portal do Cliente vive sob /portal/* com arvore de rotas e bundle
 * proprios (docs/PORTAL-CLIENTE.md secao 8). O ramo e decidido aqui, ANTES
 * de qualquer hook do escritorio rodar: `App()` em si nao chama hooks,
 * entao a escolha de qual sub-arvore montar (`EscritorioApp` ou
 * `PortalApp`) nao viola regras de hooks, e o cliente final nunca aciona
 * `useBootstrapOnMount()` / `GET /api/auth/me` do escritorio.
 */
export default function App() {
  const isPortalRoute =
    typeof window !== 'undefined' &&
    window.location.pathname.startsWith('/portal');

  if (isPortalRoute) {
    return (
      <Suspense fallback={<PageFallback />}>
        <PortalApp />
      </Suspense>
    );
  }

  return <EscritorioApp />;
}
